import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "react-router-dom";
import { X, Loader2, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { PRODUCTS, FLEET_SIZE_OPTIONS, getProduct } from "../data/content";
import { EnquiryError, submitDemoRequest } from "../services/forms";
import { businessEmailSchema, captchaConfigured, captchaSchema, phoneSchema } from "../services/fieldRules";
import { trackEvent, EVENTS } from "../services/analytics";
import { TurnstileField } from "./TurnstileField";

const buildSchema = (productId) =>
  z.object({
    fullName: z.string().min(2, "Please enter your full name").max(100),
    businessEmail: businessEmailSchema,
    company: z.string().min(2, "Please enter your company name").max(120),
    phone: phoneSchema,
    fleetSize: productId === "moveos"
      ? z.string().min(1, "Please select your fleet size")
      : z.string().optional(),
    requirement: z.string().max(200).optional(),
    message: z.string().max(2000).optional(),
    consent: z.literal(true, { errorMap: () => ({ message: "Please accept the privacy acknowledgement" }) }),
    captchaToken: captchaSchema,
  });

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-slate-700">
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`${htmlFor}-error`}>
        {error.message}
      </p>
    )}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-blue-600";

export const DemoModal = ({ open, productId, onClose }) => {
  const product = productId ? getProduct(productId) : null;
  const [phase, setPhase] = useState("form"); // form | sending | success | error
  const [referenceId, setReferenceId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(productId || "moveos");
  const location = useLocation();

  const schema = useMemo(() => buildSchema(selectedProduct), [selectedProduct]);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors },
    setFocus,
  } = useForm({ resolver: zodResolver(schema), defaultValues: { consent: false, captchaToken: "" } });

  useEffect(() => {
    if (open) {
      setPhase("form");
      setReferenceId(null);
      setSelectedProduct(productId || "moveos");
      reset({ consent: false, captchaToken: "" });
      trackEvent(EVENTS.DEMO_FORM_START, { product: productId || "unspecified" });
      setTimeout(() => setFocus("fullName"), 150);
    }
  }, [open, productId, reset, setFocus]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onSubmit = async (values) => {
    if (!captchaConfigured) {
      setError("captchaToken", { type: "setup", message: "The security check is not set up yet. Nothing was sent." });
      setPhase("form");
      return;
    }
    setPhase("sending");
    try {
      const res = await submitDemoRequest({
        type: "demo",
        product: selectedProduct,
        ...values,
        sourcePage: location.pathname,
        campaign: new URLSearchParams(window.location.search).get("utm_campaign"),
      });
      setReferenceId(res.referenceId);
      setPhase("success");
      trackEvent(EVENTS.DEMO_FORM_SUBMIT, { product: selectedProduct });
    } catch (err) {
      if (err instanceof EnquiryError && err.code === "VALIDATION" && err.errors) {
        const fields = ["fullName", "businessEmail", "company", "phone", "fleetSize", "requirement", "message", "consent", "captcha"];
        const mapped = fields.some((field) => {
          if (!err.errors[field]) return false;
          const formField = field === "captcha" ? "captchaToken" : field;
          setError(formField, { type: "server", message: err.errors[field] });
          return true;
        });
        setPhase(mapped ? "form" : "error");
        return;
      }
      setPhase("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="demo-modal-overlay"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Book a demo"
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="demo-modal"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
                  {product ? `Book a ${product.name} demo` : "Book a product demo"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Tell us a little about your business and we'll arrange a walkthrough.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close demo form"
                data-testid="demo-modal-close"
                className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-800"
              >
                <X size={16} />
              </button>
            </div>

            {phase === "success" ? (
              <div className="py-8 text-center" data-testid="demo-success-state">
                <CheckCircle2 className="mx-auto text-teal-600" size={44} />
                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Request received</h3>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-600">
                  Thank you — your demo request has been recorded with reference{" "}
                  <span className="font-mono text-xs font-semibold text-slate-900">{referenceId}</span>.
                  Our team will be in touch.
                </p>
                <button type="button" onClick={onClose} className="btn-primary mt-6" data-testid="demo-success-close">
                  Done
                </button>
              </div>
            ) : phase === "error" ? (
              <div className="py-8 text-center" data-testid="demo-error-state">
                <AlertTriangle className="mx-auto text-amber-600" size={44} />
                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Something went wrong</h3>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-600">
                  Your request could not be submitted. Nothing has been sent. Please try
                  again — your details are still in the form.
                </p>
                <div className="mt-6 flex justify-center gap-3">
                  <button type="button" onClick={() => setPhase("form")} className="btn-primary" data-testid="demo-error-retry">
                    <RotateCcw size={15} /> Try again
                  </button>
                  <button type="button" onClick={onClose} className="btn-secondary" data-testid="demo-error-close">
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate data-testid="demo-form">
                <Field label="Product" htmlFor="demo-product">
                  <select
                    id="demo-product"
                    className={inputCls}
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    data-testid="demo-product-select"
                  >
                    {PRODUCTS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.category}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name *" htmlFor="demo-full-name" error={errors.fullName}>
                    <input id="demo-full-name" className={inputCls} autoComplete="name" {...register("fullName")} data-testid="demo-full-name-input" />
                  </Field>
                  <Field label="Business email *" htmlFor="demo-email" error={errors.businessEmail}>
                    <input id="demo-email" type="email" className={inputCls} autoComplete="email" {...register("businessEmail")} data-testid="demo-email-input" />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Company name *" htmlFor="demo-company" error={errors.company}>
                    <input id="demo-company" className={inputCls} autoComplete="organization" {...register("company")} data-testid="demo-company-input" />
                  </Field>
                  <Field label="Phone number *" htmlFor="demo-phone" error={errors.phone}>
                    <input id="demo-phone" type="tel" className={inputCls} autoComplete="tel" placeholder="+91 98765 43210" {...register("phone")} data-testid="demo-phone-input" />
                  </Field>
                </div>
                {selectedProduct === "moveos" && (
                  <>
                    <Field label="Approximate fleet size *" htmlFor="demo-fleet-size" error={errors.fleetSize}>
                      <select id="demo-fleet-size" className={inputCls} defaultValue="" {...register("fleetSize")} data-testid="demo-fleet-size-select">
                        <option value="" disabled>
                          Select fleet size
                        </option>
                        {FLEET_SIZE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o} vehicles
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Primary requirement" htmlFor="demo-requirement" error={errors.requirement}>
                      <input
                        id="demo-requirement"
                        className={inputCls}
                        placeholder="e.g. Trip tracking, fuel management"
                        {...register("requirement")}
                        data-testid="demo-requirement-input"
                      />
                    </Field>
                  </>
                )}
                <Field label="Anything else we should know? (optional)" htmlFor="demo-message" error={errors.message}>
                  <textarea id="demo-message" rows={3} className={inputCls} {...register("message")} data-testid="demo-message-input" />
                </Field>
                <div>
                  <label className="flex items-start gap-2.5 text-xs text-slate-600" htmlFor="demo-consent">
                    <input
                      id="demo-consent"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
                      {...register("consent")}
                      data-testid="demo-consent-checkbox"
                    />
                    <span>
                      I agree that ZasyaOnline may use these details to respond to my
                      demo request, as described in the Privacy Policy.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="mt-1 text-xs text-red-600" role="alert" data-testid="demo-consent-error">
                      {errors.consent.message}
                    </p>
                  )}
                </div>
                <TurnstileField
                  resetKey={`${open}-${phase}`}
                  error={errors.captchaToken?.message}
                  onToken={(token) => setValue("captchaToken", token, { shouldValidate: true })}
                />
                <button
                  type="submit"
                  className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={phase === "sending"}
                  data-testid="demo-form-submit-button"
                >
                  {phase === "sending" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Submitting…
                    </>
                  ) : (
                    "Request demo"
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
